import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService, DynamicMigrationDetail } from './dynamic-api.service';
import { buildMigrationSummaryBullets } from './dynamic-migration-summary.util';
import { DynamicEntity } from './dynamic.models';

@Component({
  standalone: true,
  selector: 'ain-dynamic-migration-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    ButtonModule,
    CheckboxModule,
    ConfirmDialogModule,
    DialogModule,
    TagModule,
    FormsModule,
    RouterLink
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <ain-page
      [eyebrow]="t('dynamic.migrations.title')"
      [title]="t('dynamic.migrations.detail')"
      [description]="t('dynamic.migration.info.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'dynamic.migration.info.title'"
        [descriptionKey]="'dynamic.migration.info.description'"
        [bulletKeys]="[
          'dynamic.migration.info.bullets.review',
          'dynamic.migration.info.bullets.backup',
          'dynamic.migration.info.bullets.rollback'
        ]" />

      <a class="back-link" routerLink="/dynamic/migrations">{{ t('common.back') }}</a>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else if (migration(); as m) {
        <header class="migration-header card">
          <div class="header-top">
            <p-tag [value]="statusLabel(m.status)" [severity]="statusSeverity(m.status)" />
            @if (m.isDestructive) {
              <p-tag [value]="t('dynamic.migration.isDestructive')" severity="danger" />
            }
          </div>
          <h2>{{ m.changeDescription || t('dynamic.migration.summary.fallback') }}</h2>
          <div class="meta">
            <span>{{ t('dynamic.migration.entity') }}: <strong>{{ entityLabel() }}</strong></span>
            <span>{{ t('dynamic.migration.versionTransition') }}: <strong>{{ m.fromVersion }} → {{ m.toVersion }}</strong></span>
          </div>
        </header>

        @if (requiresBackup()) {
          <div class="destructive-banner" role="alert">
            <strong>{{ t('dynamic.migration.destructiveWarning.title') }}</strong>
            <p>{{ t('dynamic.migration.destructiveWarning.description') }}</p>
          </div>
        }

        <section class="card summary-panel">
          <h3>{{ t('dynamic.migration.summary.title') }}</h3>
          <ul>
            @for (bullet of summaryBullets(); track bullet) {
              <li>{{ bullet }}</li>
            }
          </ul>
        </section>

        <details class="advanced-preview card">
          <summary>{{ t('dynamic.migration.advancedPreview.title') }}</summary>
          <p class="hint">{{ t('dynamic.migration.advancedPreview.hint') }}</p>
          <div class="sql-block">
            <h4>{{ t('dynamic.migration.generatedSql') }}</h4>
            <pre>{{ m.generatedSql }}</pre>
          </div>
          @if (m.rollbackSql?.trim()) {
            <div class="sql-block">
              <h4>{{ t('dynamic.migration.rollbackSql') }}</h4>
              <pre>{{ m.rollbackSql }}</pre>
            </div>
          }
        </details>

        <div class="actions">
          @if (canApprove(m)) {
            <button pButton type="button" [label]="t('dynamic.approve')" (click)="confirmApprove()"></button>
          }
          @if (canExecute(m)) {
            <button pButton type="button" severity="success" [label]="t('dynamic.execute')" (click)="openExecuteDialog()"></button>
          }
          @if (canRollback(m)) {
            <button pButton type="button" severity="danger" [label]="t('dynamic.rollback')" (click)="confirmRollback()"></button>
          }
        </div>
      }

      <p-dialog
        [header]="t('dynamic.migration.executeDialog.title')"
        [(visible)]="executeDialogVisible"
        [modal]="true"
        [style]="{ width: 'min(480px, 92vw)' }">
        <p>{{ executeDialogMessage() }}</p>
        @if (requiresBackup()) {
          <div class="backup-check">
            <p-checkbox
              inputId="backupConfirmed"
              [(ngModel)]="backupConfirmed"
              [binary]="true" />
            <label for="backupConfirmed">{{ t('dynamic.migration.executeDialog.backupCheckbox') }}</label>
          </div>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="executeDialogVisible = false"></button>
          <button
            pButton
            type="button"
            severity="success"
            [label]="t('dynamic.execute')"
            [disabled]="executing() || (requiresBackup() && !backupConfirmed)"
            (click)="executeMigration()"></button>
        </ng-template>
      </p-dialog>

      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`
    .back-link { display: inline-block; margin-bottom: 12px; color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; margin-bottom: 12px; }
    .migration-header { display: grid; gap: 8px; }
    .header-top { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .migration-header h2 { margin: 0; font-size: 1.1rem; }
    .meta { display: flex; gap: 16px; flex-wrap: wrap; color: #374151; }
    .destructive-banner { border: 1px solid #fecaca; background: #fef2f2; border-radius: 10px; padding: 12px; margin-bottom: 12px; color: #991b1b; }
    .destructive-banner p { margin: 6px 0 0; }
    .summary-panel h3 { margin: 0 0 8px; font-size: 1rem; }
    .summary-panel ul { margin: 0; padding-inline-start: 1.25rem; display: grid; gap: 6px; }
    .advanced-preview summary { cursor: pointer; font-weight: 600; }
    .advanced-preview .hint { color: #6b7280; font-size: 0.92rem; }
    .sql-block h4 { margin: 12px 0 6px; font-size: 0.92rem; color: #374151; }
    pre { background: #f4f4f5; padding: 12px; overflow: auto; max-height: 320px; border-radius: 8px; margin: 0; white-space: pre-wrap; word-break: break-word; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
    .backup-check { display: flex; gap: 8px; align-items: flex-start; margin-top: 12px; }
    .backup-check label { cursor: pointer; line-height: 1.4; }
  `]
})
export class DynamicMigrationDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);

  readonly migration = signal<DynamicMigrationDetail | null>(null);
  readonly entities = signal<DynamicEntity[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly executing = signal(false);

  executeDialogVisible = false;
  backupConfirmed = false;

  private migrationId = '';

  readonly requiresBackup = computed(() => {
    const m = this.migration();
    return !!m?.isDestructive;
  });

  readonly summaryBullets = computed(() => {
    const m = this.migration();
    if (!m) {
      return [];
    }

    return buildMigrationSummaryBullets(m.generatedSql, m.changeDescription, key => this.t(key));
  });

  readonly entityLabel = computed(() => {
    const m = this.migration();
    if (!m) {
      return '—';
    }

    const entity = this.entities().find(item => item.id === m.dynamicEntityId);
    return entity?.displayName ?? entity?.name ?? m.dynamicEntityId;
  });

  ngOnInit(): void {
    this.api.listEntities().subscribe({
      next: items => this.entities.set(items),
      error: () => this.entities.set([])
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.migrationId = params.get('id') ?? '';
      if (this.migrationId) {
        this.loadMigration();
      }
    });
  }

  executeDialogMessage(): string {
    return this.requiresBackup()
      ? this.t('dynamic.migration.confirm.executeDestructive')
      : this.t('dynamic.migration.confirm.execute');
  }

  statusLabel(status: string): string {
    const key = `dynamic.migration.status.${this.normalizeStatusKey(status)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (status) {
      case 'PendingApproval':
        return 'warn';
      case 'Approved':
      case 'Executing':
        return 'info';
      case 'Succeeded':
        return 'success';
      case 'Failed':
        return 'danger';
      case 'RolledBack':
        return 'secondary';
      case 'RollbackRequested':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  canApprove(m: DynamicMigrationDetail): boolean {
    return m.status === 'PendingApproval' && this.hasMigrationAction('Approve');
  }

  canExecute(m: DynamicMigrationDetail): boolean {
    return m.status === 'Approved' && this.hasMigrationAction('Execute');
  }

  canRollback(m: DynamicMigrationDetail): boolean {
    return m.status === 'Succeeded'
      && !!m.rollbackSql?.trim()
      && this.hasMigrationAction('Rollback');
  }

  confirmApprove(): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.migration.confirm.approve'),
      accept: () => this.approveMigration()
    });
  }

  openExecuteDialog(): void {
    if (this.requiresBackup()) {
      this.backupConfirmed = false;
      this.executeDialogVisible = true;
      return;
    }

    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.migration.confirm.execute'),
      accept: () => this.executeMigration(false)
    });
  }

  confirmRollback(): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.migration.confirm.rollback'),
      accept: () => this.rollbackMigration()
    });
  }

  executeMigration(fromDialog = true): void {
    const id = this.migration()?.id;
    if (!id) {
      return;
    }

    if (this.requiresBackup() && !this.backupConfirmed) {
      return;
    }

    this.executing.set(true);
    this.api.executeMigration(id, { backupConfirmed: this.requiresBackup() ? this.backupConfirmed : false }).pipe(
      finalize(() => this.executing.set(false))
    ).subscribe({
      next: () => {
        if (fromDialog) {
          this.executeDialogVisible = false;
        }
        this.messages.add({ severity: 'success', summary: this.t('dynamic.executed') });
        this.loadMigration();
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveActionError(err) })
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private approveMigration(): void {
    const id = this.migration()?.id;
    if (!id) {
      return;
    }

    this.api.approveMigration(id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.migration.approved') });
        this.loadMigration();
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveActionError(err) })
    });
  }

  private rollbackMigration(): void {
    const id = this.migration()?.id;
    if (!id) {
      return;
    }

    this.api.rollbackMigration(id).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.migration.rolledBack') });
        this.loadMigration();
      },
      error: err => this.messages.add({ severity: 'error', summary: this.resolveActionError(err) })
    });
  }

  private loadMigration(): void {
    if (!this.migrationId) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.api.getMigration(this.migrationId).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: item => this.migration.set(item),
      error: err => {
        this.migration.set(null);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  private normalizeStatusKey(status: string): string {
    switch (status) {
      case 'PendingApproval':
        return 'pending';
      case 'Approved':
        return 'approved';
      case 'Executing':
        return 'executing';
      case 'Succeeded':
        return 'executed';
      case 'Failed':
        return 'failed';
      case 'RolledBack':
        return 'rolledBack';
      case 'RollbackRequested':
        return 'rollbackRequested';
      default:
        return status.charAt(0).toLowerCase() + status.slice(1);
    }
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code) {
      const validationKey = `dynamic.validation.${code}`;
      const validationMessage = this.t(validationKey);
      if (validationMessage !== validationKey) {
        return validationMessage;
      }
    }

    if (error instanceof HttpErrorResponse) {
      const body = error.error as Record<string, unknown> | null;
      const message = typeof body?.['Message'] === 'string' ? body['Message'].trim() : '';
      if (message) {
        return message;
      }
    }

    return this.t('common.saveFailed');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    const raw = body?.['Error'] ?? body?.['error'] ?? body?.['code'];
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  }

  private hasMigrationAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.migrations');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }
}
